import { CalendarClock, Repeat, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type BookingType = 'one_time' | 'recurring';

interface BookingTypeStepProps {
  value: BookingType;
  onChange: (v: BookingType) => void;
  t: (k: string) => string;
}

export function BookingTypeStep({ value, onChange, t }: BookingTypeStepProps) {
  const options: Array<{
    id: BookingType;
    icon: React.ReactNode;
    title: string;
    description: string;
    testId: string;
  }> = [
    {
      id: 'one_time',
      icon: <CalendarClock className="h-5 w-5" />,
      title: t('newBooking.bookingType.oneTime'),
      description: t('newBooking.bookingType.oneTimeHint'),
      testId: 'booking-type-one-time',
    },
    {
      id: 'recurring',
      icon: <Repeat className="h-5 w-5" />,
      title: t('newBooking.bookingType.recurring'),
      description: t('newBooking.bookingType.recurringHint'),
      testId: 'booking-type-recurring',
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            data-testid={opt.testId}
            aria-pressed={selected}
            className={cn(
              'flex items-start gap-3 rounded-2xl border bg-surface p-4 text-left transition-colors',
              selected
                ? 'border-brand-500 bg-brand-50 shadow-sm'
                : 'border-line hover:border-brand-300 hover:bg-surface-2',
            )}
          >
            <span
              className={cn(
                'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-pill',
                selected ? 'bg-brand-500 text-white' : 'bg-brand-50 text-brand-700',
              )}
            >
              {opt.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-body font-semibold text-ink">{opt.title}</span>
              <span className="mt-0.5 block text-body-sm text-soft">{opt.description}</span>
            </span>
            <span
              className={cn(
                'mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                selected ? 'border-brand-500 bg-brand-500 text-white' : 'border-line',
              )}
              aria-hidden="true"
            >
              {selected && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
